import React, {useState, useEffect} from 'react';
import useOptimizedConfig from './useOptimizedConfig';
import {initializeURLBuilder} from '@/utils/urlBuilder';

interface ConfigState {
    FCONST: Record<string, string>;
    SCONSTANTS: Record<string, string>;
    DCONSTANTS: Record<string, string>;
    URLS: Record<string, string>;
}

interface UseConfigReturn {
    config: ConfigState;
    isLoaded: boolean;
    isLoading: boolean;
}

const defaultConfig: ConfigState = {
    FCONST: {
        STATIC_URL: '/static/',
        SITENAME: 'Vision',
        DEBUG: 'false',
        FLASKPAGE: 'true',
        FS_FRONTEND_TRANSLATION_DEBUG_MODE: 'false',
        FS_FRONTEND_PERMISSONS_DEBUG_MODE: 'false',
        FS_FRONTEND_SESSION_EXPIRY_DEBUG_MODE: 'false',
        BABEL_DEFAULT_LOCALE: 'en',
        FS_SESSION_EXPIRY_WARNING_TIME_IN_SECONDS: '30',
        PERIOD_DEFAULT_LENGTH: 'this_month'
    },
    SCONSTANTS: {},
    DCONSTANTS: {
        sessionTime: '14400', // 4 hours
        datetimeFormat: 'yyyy-LL-dd HH:mm:ss',
        maxChartLegendLabelsShown: '40',
        monitorLastDays: '60',
        helpInformation: 'false'
    },
    URLS: {}
};

export const useConfig = (): UseConfigReturn => {
    const [config, setConfig] = useState<ConfigState>(defaultConfig);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const handleConfigLoaded = (event: CustomEvent) => {
            const serverConfig = event.detail?.config;
            if (serverConfig) {
                const newConfig = {
                    FCONST: {...defaultConfig.FCONST, ...serverConfig.FCONST},
                    SCONSTANTS: {...defaultConfig.SCONSTANTS, ...serverConfig.SCONSTANTS},
                    DCONSTANTS: {...defaultConfig.DCONSTANTS, ...serverConfig.DCONSTANTS},
                    URLS: {...defaultConfig.URLS, ...serverConfig.URLS}
                };
                setConfig(newConfig);
                
                // Initialize URL builder with new URLs
                if (newConfig.URLS && Object.keys(newConfig.URLS).length > 0) {
                    initializeURLBuilder(newConfig.URLS);
                }
            }
            setIsLoaded(true);
            setIsLoading(false);
        };

        // Listen for config loaded event
        window.addEventListener('configLoaded', handleConfigLoaded as EventListener);
        
        // Check if config is already loaded (including empty DCONSTANTS)
        if (window.DCONSTANTS !== undefined) {
            const existingConfig = {
                FCONST: {...defaultConfig.FCONST, ...window.FCONST},
                SCONSTANTS: {...defaultConfig.SCONSTANTS, ...window.SCONSTANTS},
                DCONSTANTS: {...defaultConfig.DCONSTANTS, ...window.DCONSTANTS},
                URLS: {...defaultConfig.URLS, ...window.URLS}
            };
            setConfig(existingConfig);
            
            // Initialize URL builder with existing URLs
            if (existingConfig.URLS && Object.keys(existingConfig.URLS).length > 0) {
                initializeURLBuilder(existingConfig.URLS);
            }
            
            setIsLoaded(true);
            setIsLoading(false);
        }

        return () => {
            window.removeEventListener('configLoaded', handleConfigLoaded as EventListener);
        };
    }, []);

    return {config, isLoaded, isLoading};
};

// Convenience hooks for specific config sections
export const useDConstants = () => {
    const {config, isLoaded, isLoading} = useConfig();
    return {
        dconstants: config.DCONSTANTS,
        isLoaded,
        isLoading
    };
};

export const useFConstants = () => {
    const {config, isLoaded, isLoading} = useConfig();
    return {
        fconst: config.FCONST,
        isLoaded,
        isLoading
    };
};

export const useUrls = () => {
    const {config, isLoaded, isLoading} = useConfig();
    
    
    return {
        urls: config.URLS,
        isLoaded,
        isLoading
    };
};

// Specific hooks for commonly used values
export const useSessionConfig = () => {
    const {dconstants, isLoaded} = useDConstants();
    const {fconst} = useFConstants();
    
    return {
        sessionTime: Number(dconstants.sessionTime) * 1000, // Convert to milliseconds
        warningTime: Number(fconst.FS_SESSION_EXPIRY_WARNING_TIME_IN_SECONDS),
        isLoaded
    };
};

export const useDateTimeFormat = () => {
    const {dconstants, isLoaded} = useDConstants();
    
    return {
        format: dconstants.datetimeFormat,
        isLoaded
    };
};

export const useChartConfig = () => {
    const {dconstants, isLoaded} = useDConstants();
    
    return {
        maxLegendLabels: Number(dconstants.maxChartLegendLabelsShown),
        isLoaded
    };
};

export const usePeriodConfig = () => {
    // Use useOptimizedConfig instead of useFConstants since it's working correctly
    const {config, loading} = useOptimizedConfig();
    const isLoaded = !loading && !!config;
    const periodDefaultLength = config?.FCONST?.PERIOD_DEFAULT_LENGTH;
    
    
    return {
        periodDefaultLength,
        isLoaded
    };
};
